/**
 * TASK-PBG-003 & TASK-PBG-004: Parallax Updater
 *
 * Functions to update parallax layer positions and zoom scales based on camera state.
 *
 * Position Update (TASK-PBG-003):
 * - Applies parallax factor to camera offset from base position
 * - Updates TilingSprite.tilePosition to create parallax effect
 * - Negated because tilePosition moves opposite to camera direction
 *
 * Zoom Update (TASK-PBG-004):
 * - Scales layer texture based on camera zoom
 * - Multiplies base scale by zoom factor
 * - Maintains visual consistency during zoom operations
 */

import * as PIXI from 'pixi.js';
import type { ParallaxLayerConfig, CameraState } from '../../types/terrain';

/**
 * Update parallax layer position based on camera movement
 *
 * This function applies the parallax factor to create depth perception.
 * Layers with lower parallax factors move slower than the camera, creating
 * the illusion that they are farther away.
 *
 * For TilingSprites, updates tilePosition.
 * For regular Sprites, updates the sprite's x/y position directly.
 *
 * @param layer - The TilingSprite or Sprite to update
 * @param camera - Current camera state
 * @param config - Parallax layer configuration
 * @param basePosition - Base/reference camera position (usually initial camera position)
 *
 * @example
 * ```typescript
 * // Far layer moves 10% of camera movement
 * updateParallaxPosition(
 *   farLayer,
 *   { x: 100, y: 50, zoom: 1 },
 *   { parallaxFactor: 0.1, ... },
 *   { x: 0, y: 0 }
 * );
 * // farLayer.tilePosition will be (-10, -5) for TilingSprite
 * // or farLayer position will be adjusted for regular Sprite
 * ```
 */
/**
 * Store initial sprite positions for parallax calculations
 * Maps sprite instances to their initial center positions
 */
const spriteBasePositions = new WeakMap<PIXI.Sprite, { x: number; y: number }>();

/**
 * Initialize a sprite's base position for parallax tracking
 * Should be called when creating a sprite for parallax use
 */
export function initializeSpriteBasePosition(sprite: PIXI.Sprite): void {
  spriteBasePositions.set(sprite, { x: sprite.x, y: sprite.y });
}

export function updateParallaxPosition(
  layer: PIXI.TilingSprite | PIXI.Sprite,
  camera: CameraState,
  config: ParallaxLayerConfig,
  basePosition: { x: number; y: number }
): void {
  // Calculate offset from base position
  const offsetX = camera.x - basePosition.x;
  const offsetY = camera.y - basePosition.y;

  // Apply parallax factor
  const parallaxOffsetX = -offsetX * config.parallaxFactor;
  const parallaxOffsetY = -offsetY * config.parallaxFactor;

  if (layer instanceof PIXI.TilingSprite) {
    // For TilingSprite, update tilePosition
    // (tilePosition moves opposite to camera movement)
    layer.tilePosition.x = parallaxOffsetX;
    layer.tilePosition.y = parallaxOffsetY;
  } else {
    // For regular Sprite, update position relative to base position
    const basePos = spriteBasePositions.get(layer);
    if (basePos) {
      layer.x = basePos.x + parallaxOffsetX;
      layer.y = basePos.y + parallaxOffsetY;
    }
  }
}

/**
 * Update parallax layer zoom scale based on camera zoom
 *
 * This function scales the layer's texture to match the camera zoom level,
 * ensuring the parallax layers remain visually consistent when zooming.
 *
 * For TilingSprites, updates tileScale.
 * For regular Sprites, updates the sprite's scale directly.
 *
 * @param layer - The TilingSprite or Sprite to update
 * @param camera - Current camera state
 * @param config - Parallax layer configuration
 *
 * @example
 * ```typescript
 * // Scale layer texture with camera zoom
 * updateParallaxZoom(
 *   farLayer,
 *   { x: 0, y: 0, zoom: 2.0 },
 *   { scale: 1.0, ... }
 * );
 * // farLayer.tileScale will be (2.0, 2.0) for TilingSprite
 * // or farLayer.scale will be (2.0, 2.0) for Sprite
 * ```
 */
export function updateParallaxZoom(
  layer: PIXI.TilingSprite | PIXI.Sprite,
  camera: CameraState,
  config: ParallaxLayerConfig
): void {
  if (layer instanceof PIXI.TilingSprite) {
    // For TilingSprite, update tileScale
    if (config.tileScale) {
      layer.tileScale.x = config.tileScale.x * camera.zoom;
      layer.tileScale.y = config.tileScale.y * camera.zoom;
    } else {
      layer.tileScale.set(config.scale * camera.zoom);
    }
  } else {
    // For regular Sprite, update scale directly
    // Note: The sprite's base scale was already calculated in the factory to cover the bounds
    // We need to multiply that by the camera zoom
    // The factory already set sprite.scale based on cover-fit calculation
    // We need to store and retrieve the base scale
    const baseScale = (layer as any).__baseScale ?? config.scale;
    layer.scale.set(baseScale * camera.zoom);
  }
}
